import React, { useCallback, useState } from "react";
import styled from "styled-components";
import moment, { Moment } from "moment";
import { DateRangePicker, FocusedInputShape } from "react-dates";
import { UseFormReturn } from "react-hook-form";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { FunctionalityReportFormInput } from "../index";
import { ErrorFieldMessage } from "../shared/components/ErrorFieldMessage";

const messages = defineMessages({
  title: {
    id: "functionalityReport.period.title",
    defaultMessage: "Period",
  },
  startDate: {
    id: "functionalityReport.period.startDate",
    defaultMessage: "Start Date",
  },
  endDate: {
    id: "functionalityReport.period.endDate",
    defaultMessage: "End Date",
  },
});

const DateRangePickerWrapper = styled.div<{ hasError: boolean }>`
  .DateRangePicker {
    border-radius: 5px;
  }

  .DateRangePickerInput {
    border-radius: 5px;
    border-color: hsl(0, 0%, 80%);
    width: 240px;
    padding: 1px;
  }

  .DateInput {
    border-radius: 5px;
  }

  .DateInput_input {
    font-size: 12px;
    font-weight: normal;
  }

  .DateRangePickerInput_clearDates_svg {
    width: auto;
  }
`;

const WitLabel = styled.label`
  display: block;
  width: 100%;
  padding: 0;
  margin: 0 0 0 0;
  font-size: 21px;
  line-height: inherit;
  color: #3a3f51;
  border: 0;
  border-bottom: 1px solid #e5e5e5;
`;

type DateRange = {
  startDate?: moment.Moment;
  endDate?: moment.Moment;
};

export function FunctionalityPeriod({
  setValue,
  formState: { errors },
}: UseFormReturn<FunctionalityReportFormInput>) {
  const intl = useIntl();
  const [dates, setDates] = useState<DateRange | undefined>();
  const [datePickerFocusState, setDatePickerFocusState] = useState<FocusedInputShape | null>(null);

  const onDatesChange = useCallback(
    ({ startDate, endDate }: { endDate: Moment | null; startDate: Moment | null }) => {
      setDates({ startDate: startDate || undefined, endDate: endDate || undefined });
      setValue("startDate", startDate?.format("YYYY-MM-DD") ?? "", { shouldValidate: true });
      setValue("endDate", endDate?.format("YYYY-MM-DD") ?? "", { shouldValidate: true });
    },
    [setValue]
  );

  return (
    <>
      <WitLabel>
        <FormattedMessage {...messages.title} />
      </WitLabel>
      <DateRangePickerWrapper hasError={false}>
        <DateRangePicker
          startDate={dates?.startDate ?? null}
          startDatePlaceholderText={intl.formatMessage(messages.startDate)}
          startDateId={"eventStart"}
          endDate={dates?.endDate ?? null}
          endDatePlaceholderText={intl.formatMessage(messages.endDate)}
          endDateId={"eventEnd"}
          focusedInput={datePickerFocusState}
          onFocusChange={setDatePickerFocusState}
          isOutsideRange={() => false}
          displayFormat="DD/MM/YYYY"
          onDatesChange={onDatesChange}
          minimumNights={0}
          small
          showClearDates
          withPortal
        />
      </DateRangePickerWrapper>
      <ErrorFieldMessage errors={errors} name={`startDate`} />
      <ErrorFieldMessage errors={errors} name={`endDate`} />
    </>
  );
}
